/**
 * AdminPanel — multi-tab admin dashboard for platform_owner and tenant_owner.
 *
 * Tabs:
 *   1. Users      — list, suspend/unsuspend, deactivate, allocate tokens, change role
 *   2. Tokens     — org pool overview, per-user usage bars, transfer dialog
 *   3. Costs      — LLM cost analytics: per user / model / endpoint
 *   4. Subscriptions — current plan, Stripe Portal link, change plan
 *   5. Tenants    — platform_owner only: all tenants list + license editor
 *   6. Audit log  — admin actions trail
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Coins, BarChart3, CreditCard, Building, Shield, Pause, Play,
  UserX, UserCheck, RefreshCw, ArrowRightLeft, AlertTriangle, TrendingUp, Bug, Trash2
} from 'lucide-react';
import ReportsAdminTab from '../components/admin/ReportsAdminTab';
import {
  adminListUsers, adminSuspendUser, adminUnsuspendUser, adminDeactivateUser, adminReactivateUser,
  adminChangeRole, adminChangeTenant, adminAllocateTokens, adminTransferTokens, adminOrgTokenStatus,
  adminListTenants, adminUpdateTenantLicense, adminCostBreakdown, adminAuditLog, getMe,
  adminScopeOptions, adminSetTenantScope, adminDeleteUser
} from '../lib/api';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);
const fmtNum = (n) => (n == null ? '—' : n === -1 ? '∞' : Number(n).toLocaleString('pl-PL'));
const fmtPLN = (n) => (n == null ? '—' : `${Number(n).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} zł`);

export default function AdminPanel({ lang, tenantId }) {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('users');

  useEffect(() => {
    getMe().then(setMe).catch(() => setMe({ role: 'viewer' }));
  }, []);

  if (!me) {
    return <div className="p-6 text-center text-muted-foreground">{t(lang, 'Ładowanie…', 'Loading…')}</div>;
  }

  const isPlatform = me.role === 'platform_owner';
  const isOwner = me.role === 'owner' || isPlatform;

  if (!isOwner && me.role !== 'admin') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
            <h2 className="text-lg font-medium">{t(lang, 'Brak dostępu', 'Access denied')}</h2>
            <p className="text-sm text-muted-foreground mt-2">
              {t(lang,
                'Panel administracyjny dostępny tylko dla Owner/Admin/Platform Owner.',
                'Admin panel restricted to Owner/Admin/Platform Owner roles.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Shield className="w-6 h-6 text-primary" />
            {t(lang, 'Panel administracyjny', 'Admin Panel')}
            {isPlatform && (
              <Badge className="bg-purple-500/15 text-purple-300">Platform Owner</Badge>
            )}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {t(lang, 'Zarządzanie użytkownikami, tokenami, kosztami i subskrypcjami', 'User, tokens, costs & subscription management')}
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users"><Users className="w-3.5 h-3.5 mr-1" />{t(lang, 'Użytkownicy', 'Users')}</TabsTrigger>
          <TabsTrigger value="tokens"><Coins className="w-3.5 h-3.5 mr-1" />Tokeny</TabsTrigger>
          <TabsTrigger value="costs"><BarChart3 className="w-3.5 h-3.5 mr-1" />{t(lang, 'Koszty', 'Costs')}</TabsTrigger>
          <TabsTrigger value="subscription"><CreditCard className="w-3.5 h-3.5 mr-1" />Subskrypcja</TabsTrigger>
          {isPlatform && (
            <TabsTrigger value="tenants"><Building className="w-3.5 h-3.5 mr-1" />Tenants</TabsTrigger>
          )}
          <TabsTrigger value="reports"><Bug className="w-3.5 h-3.5 mr-1" />Zgłoszenia</TabsTrigger>
          <TabsTrigger value="audit">{t(lang, 'Audit log', 'Audit log')}</TabsTrigger>
        </TabsList>

        <TabsContent value="users"><UsersTab lang={lang} me={me} /></TabsContent>
        <TabsContent value="tokens"><TokensTab lang={lang} me={me} /></TabsContent>
        <TabsContent value="costs"><CostsTab lang={lang} me={me} /></TabsContent>
        <TabsContent value="subscription"><SubscriptionTab lang={lang} me={me} /></TabsContent>
        {isPlatform && <TabsContent value="tenants"><TenantsTab lang={lang} /></TabsContent>}
        <TabsContent value="reports"><ReportsAdminTab lang={lang} me={me} /></TabsContent>
        <TabsContent value="audit"><AuditTab lang={lang} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ========== USERS TAB ==========
function UsersTab({ lang, me }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allocateDialog, setAllocateDialog] = useState(null);  // user object
  const [allocateAmount, setAllocateAmount] = useState('');
  const [tenants, setTenants] = useState([]);  // T49 — lista tenantów do przenoszenia

  useEffect(() => { load(); }, []);
  // T49 — platform_owner: wczytaj tenanty do dropdownu "Zmień tenant"
  useEffect(() => {
    if (me.role === 'platform_owner') {
      adminListTenants().then(r => setTenants(r.items || r.tenants || [])).catch(() => {});
    }
  }, [me.role]);

  async function load() {
    setLoading(true);
    try {
      const r = await adminListUsers({ limit: 200 });
      setUsers(r.items || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function suspend(u) {
    const reason = window.prompt(t(lang, 'Powód zawieszenia:', 'Suspension reason:'), 'Decyzja administratora');
    if (!reason) return;
    try {
      await adminSuspendUser(u.id, reason);
      toast.success(t(lang, 'Zawieszono', 'Suspended'));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }
  async function unsuspend(u) {
    try { await adminUnsuspendUser(u.id); toast.success(t(lang, 'Aktywowano', 'Reactivated')); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }
  async function deactivate(u) {
    if (!window.confirm(t(lang, `Dezaktywować ${u.email}? Konto będzie zablokowane.`, `Deactivate ${u.email}?`))) return;
    try { await adminDeactivateUser(u.id); toast.success(t(lang, 'Dezaktywowano', 'Deactivated')); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }
  async function reactivate(u) {
    try { await adminReactivateUser(u.id); toast.success('Reaktywowano'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }
  async function deleteUserAccount(u) {
    // Usuwanie = nieodwracalne → potwierdzenie przez wpisanie e-maila konta (anty-pomyłka).
    const typed = window.prompt(t(lang,
      `TRWALE usunąć konto ${u.email}? Tej operacji NIE można cofnąć. Wpisz e-mail konta, aby potwierdzić:`,
      `PERMANENTLY delete ${u.email}? This CANNOT be undone. Type the account e-mail to confirm:`));
    if (!typed) return;
    if (typed.trim().toLowerCase() !== (u.email || '').trim().toLowerCase()) {
      toast.error(t(lang, 'E-mail nie zgadza się — anulowano', 'E-mail mismatch — cancelled'));
      return;
    }
    try {
      await adminDeleteUser(u.id, u.email);
      toast.success(t(lang, 'Konto usunięte', 'Account deleted'));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }
  async function changeRole(u, role) {
    try {
      await adminChangeRole(u.id, role);
      toast.success(`Rola zmieniona: ${role}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }
  // T49 — przeniesienie usera do innego tenanta (platform_owner only)
  async function changeTenant(u, tenantId) {
    if (tenantId === u.tenant_id) return;
    const tName = (tenants.find(t => t.id === tenantId) || {}).name || tenantId.substring(0, 8);
    if (!window.confirm(t(lang,
      `Przenieść ${u.email} do tenanta "${tName}"? Użytkownik będzie musiał się przelogować. Operacja odwracalna (zapis previous_tenant_id).`,
      `Move ${u.email} to tenant "${tName}"? User must re-login.`))) return;
    try {
      await adminChangeTenant(u.id, tenantId);
      toast.success(t(lang, `Przeniesiono do: ${tName} (user musi się przelogować)`, `Moved to ${tName}`));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }
  async function allocate() {
    if (!allocateDialog) return;
    const amount = allocateAmount === '-1' ? -1 : parseInt(allocateAmount, 10);
    if (isNaN(amount)) { toast.error('Liczba'); return; }
    try {
      await adminAllocateTokens(allocateDialog.id, amount);
      toast.success(t(lang, `Przydzielono ${amount === -1 ? '∞' : amount.toLocaleString('pl-PL')} tokenów`, 'Allocated'));
      setAllocateDialog(null);
      setAllocateAmount('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }

  const isPlatform = me.role === 'platform_owner';

  return (
    <Card>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Ładowanie…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">{t(lang, 'Brak użytkowników', 'No users')}</div>
        ) : (
          <div className="divide-y divide-border">
            {users.map(u => {
              const usage = u.usage || {};
              const pct = usage.usage_pct || 0;
              return (
                <div key={u.id} className="p-3" data-testid={`admin-user-row-${u.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium ${
                      u.account_status === 'suspended' ? 'bg-amber-500/20 text-amber-300' :
                      u.account_status === 'deactivated' ? 'bg-red-500/20 text-red-300' :
                      'bg-primary/15 text-primary'
                    }`}>
                      {(u.name || u.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{u.name || u.email}</span>
                        <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                        {u.account_status === 'suspended' && (
                          <Badge className="bg-amber-500/15 text-amber-300 text-[10px]">
                            {t(lang, 'zawieszony', 'suspended')}: {u.suspension_reason}
                          </Badge>
                        )}
                        {u.account_status === 'deactivated' && (
                          <Badge className="bg-red-500/15 text-red-300 text-[10px]">dezaktywowany</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{u.email}</div>
                      {/* Token usage bar */}
                      {usage.tokens_limit !== -1 && usage.tokens_limit > 0 && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                            {fmtNum(usage.tokens_used)} / {fmtNum(usage.tokens_limit)} ({pct}%)
                          </span>
                          {usage.cost_pln_estimate > 0 && (
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                              · {fmtPLN(usage.cost_pln_estimate)}
                            </span>
                          )}
                        </div>
                      )}
                      {usage.tokens_limit === -1 && (
                        <div className="text-[10px] text-muted-foreground mt-1">
                          ∞ tokenów · {fmtNum(usage.tokens_used)} wykorzystane
                          {usage.cost_pln_estimate > 0 && ` · ${fmtPLN(usage.cost_pln_estimate)}`}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline"
                              onClick={() => { setAllocateDialog(u); setAllocateAmount(String(usage.tokens_limit ?? 0)); }}
                              title="Przydziel tokeny"
                              data-testid={`admin-allocate-${u.id}`}>
                        <Coins className="w-3.5 h-3.5" />
                      </Button>
                      {isPlatform && (
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="platform_owner">platform_owner</SelectItem>
                            <SelectItem value="owner">owner</SelectItem>
                            <SelectItem value="admin">admin</SelectItem>
                            <SelectItem value="handlowiec">handlowiec</SelectItem>
                            <SelectItem value="viewer">viewer</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {/* T49 — zmiana tenanta (platform_owner only) */}
                      {isPlatform && tenants.length > 0 && (
                        <Select value={u.tenant_id} onValueChange={(v) => changeTenant(u, v)}>
                          <SelectTrigger className="w-40 h-8 text-xs" title="Przenieś do tenanta">
                            <SelectValue placeholder="tenant" />
                          </SelectTrigger>
                          <SelectContent>
                            {tenants.map(tn => (
                              <SelectItem key={tn.id} value={tn.id}>
                                {tn.name || tn.id.substring(0, 8)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {u.account_status === 'suspended' ? (
                        <Button size="sm" variant="ghost" className="text-emerald-400" onClick={() => unsuspend(u)} title="Wznów konto">
                          <Play className="w-3.5 h-3.5" />
                        </Button>
                      ) : u.account_status !== 'deactivated' && (
                        <Button size="sm" variant="ghost" className="text-amber-400" onClick={() => suspend(u)}
                                title="Zawieś konto"
                                data-testid={`admin-suspend-${u.id}`}>
                          <Pause className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {u.account_status === 'deactivated' ? (
                        <Button size="sm" variant="ghost" className="text-emerald-400" onClick={() => reactivate(u)} title="Reaktywuj konto">
                          <UserCheck className="w-3.5 h-3.5" />
                        </Button>
                      ) : (u.id !== me.id && u.role !== 'owner' && u.role !== 'platform_owner') ? (
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deactivate(u)} title="Dezaktywuj konto">
                          <UserX className="w-3.5 h-3.5" />
                        </Button>
                      ) : null}
                      {(u.id !== me.id && u.role !== 'owner' && u.role !== 'platform_owner') && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => deleteUserAccount(u)}
                                title="Usuń konto (trwałe)"
                                data-testid={`admin-delete-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!allocateDialog} onOpenChange={(o) => !o && setAllocateDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(lang, 'Przydziel tokeny', 'Allocate tokens')}: {allocateDialog?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">{t(lang, 'Limit miesięczny', 'Monthly limit')}</Label>
            <Input type="number" value={allocateAmount} onChange={(e) => setAllocateAmount(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">
              {me.role === 'platform_owner'
                ? t(lang, 'Możesz ustawić -1 dla nieograniczonych tokenów.', 'You can set -1 for unlimited.')
                : t(lang, 'Maksymalna alokacja per użytkownik: 3 000 000. -1 (nieograniczone) tylko dla platform_owner.',
                       'Max per-user: 3M. -1 (unlimited) only for platform_owner.')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocateDialog(null)}>{t(lang, 'Anuluj', 'Cancel')}</Button>
            <Button onClick={allocate} data-testid="admin-allocate-save">{t(lang, 'Zapisz', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ========== TOKENS TAB ==========
function TokensTab({ lang, me }) {
  const [status, setStatus] = useState(null);
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferForm, setTransferForm] = useState({ from: '', to: '', amount: '' });

  useEffect(() => { load(); }, []);
  async function load() {
    try { setStatus(await adminOrgTokenStatus()); }
    catch (err) { console.error(err); }
  }

  async function transfer() {
    try {
      await adminTransferTokens(transferForm.from, transferForm.to, parseInt(transferForm.amount, 10));
      toast.success(t(lang, 'Transfer wykonany', 'Transferred'));
      setTransferDialog(false);
      setTransferForm({ from: '', to: '', amount: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  }

  if (!status) return <div className="p-6 text-center text-sm text-muted-foreground">Ładowanie…</div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Coins className="w-4 h-4 text-primary" />
              {t(lang, 'Pula tokenów', 'Token pool')}: {status.tenant_name}
              <Badge variant="outline">{status.plan}</Badge>
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setTransferDialog(true)}>
              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
              {t(lang, 'Transfer', 'Transfer')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label={t(lang, 'Pula', 'Pool')} value={fmtNum(status.pool_total)} />
            <Stat label={t(lang, 'Przydzielone', 'Allocated')} value={fmtNum(status.pool_allocated)} />
            <Stat label={t(lang, 'Dostępne', 'Available')} value={fmtNum(status.pool_available)} color="emerald" />
            <Stat label={t(lang, 'Wykorzystane', 'Used')}
                  value={`${fmtNum(status.pool_used)} (${status.pool_usage_pct}%)`}
                  color={status.pool_usage_pct >= 80 ? 'red' : 'amber'} />
          </div>
          <div className="text-xs text-muted-foreground mb-3">
            {t(lang, 'Koszt LLM w tym miesiącu:', 'LLM cost this month:')}{' '}
            <span className="font-medium text-foreground">{fmtPLN(status.total_cost_pln_estimate)}</span>
            {' '}({status.total_cost_usd_estimate} USD)
          </div>
          <div className="space-y-1">
            {status.members.map(m => (
              <MemberRow key={m.user_id} m={m} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t(lang, 'Transfer tokenów', 'Transfer tokens')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t(lang, 'Od użytkownika', 'From user')}</Label>
              <Select value={transferForm.from} onValueChange={(v) => setTransferForm({ ...transferForm, from: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {(status?.members || []).filter(m => m.allocated > 0).map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.name} ({fmtNum(m.allocated)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t(lang, 'Do użytkownika', 'To user')}</Label>
              <Select value={transferForm.to} onValueChange={(v) => setTransferForm({ ...transferForm, to: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {(status?.members || []).filter(m => m.user_id !== transferForm.from).map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t(lang, 'Liczba tokenów', 'Amount')}</Label>
              <Input type="number" value={transferForm.amount}
                     onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>{t(lang, 'Anuluj', 'Cancel')}</Button>
            <Button onClick={transfer}>{t(lang, 'Transferuj', 'Transfer')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MemberRow({ m }) {
  const pct = m.usage_pct;
  return (
    <div className="p-2 hover:bg-muted/30 rounded-md flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm">{m.name}</span>
          <Badge variant="outline" className="text-[10px]">{m.role}</Badge>
          {m.account_status === 'suspended' && (
            <Badge className="bg-amber-500/15 text-amber-300 text-[10px]">zawieszony</Badge>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">{m.email}</div>
      </div>
      <div className="w-48 flex items-center gap-2">
        {m.allocated === -1 ? (
          <span className="text-xs text-muted-foreground">∞ · {fmtNum(m.used)} użyte</span>
        ) : (
          <>
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className={`h-full ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                   style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
              {fmtNum(m.used)} / {fmtNum(m.allocated)}
            </span>
          </>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground whitespace-nowrap w-20 text-right">
        {fmtPLN(m.cost_pln)}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  const colorMap = {
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    red: 'text-red-300',
  };
  return (
    <div className="p-3 bg-muted/20 rounded-lg">
      <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${colorMap[color] || ''}`}>{value}</div>
    </div>
  );
}

// ========== COSTS TAB ==========
function CostsTab({ lang, me }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => { load(); }, [period]); // eslint-disable-line

  async function load() {
    try { setData(await adminCostBreakdown({ period })); }
    catch (err) { console.error(err); }
  }

  if (!data) return <div className="p-6 text-center text-sm text-muted-foreground">Ładowanie…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label className="text-xs">{t(lang, 'Okres', 'Period')}</Label>
        <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t(lang, 'Wywołań LLM', 'LLM calls')} value={fmtNum(data.totals.calls)} />
        <Stat label="Tokens" value={fmtNum(data.totals.tokens)} />
        <Stat label="Koszt PLN" value={fmtPLN(data.totals.cost_pln)} color="amber" />
        <Stat label="USD" value={`$${data.totals.cost_usd}`} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">{t(lang, 'Per użytkownik', 'Per user')}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-[11px] text-muted-foreground uppercase">
              <tr><th className="text-left p-2">{t(lang, 'Użytkownik', 'User')}</th>
                  <th className="text-right p-2">Tokens</th>
                  <th className="text-right p-2">{t(lang, 'Wywołań', 'Calls')}</th>
                  <th className="text-right p-2">Koszt</th></tr>
            </thead>
            <tbody>
              {data.by_user.map(u => (
                <tr key={u.user_id} className="border-t border-border">
                  <td className="p-2">{u.user_email}</td>
                  <td className="text-right p-2 tabular-nums">{fmtNum(u.tokens)}</td>
                  <td className="text-right p-2 tabular-nums">{u.calls}</td>
                  <td className="text-right p-2 tabular-nums">{fmtPLN(u.cost_pln)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t(lang, 'Per model', 'Per model')}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-[11px] text-muted-foreground uppercase">
              <tr><th className="text-left p-2">Model</th>
                  <th className="text-right p-2">In</th>
                  <th className="text-right p-2">Out</th>
                  <th className="text-right p-2">Calls</th>
                  <th className="text-right p-2">Koszt</th></tr>
            </thead>
            <tbody>
              {data.by_model.map(m => (
                <tr key={m.model} className="border-t border-border">
                  <td className="p-2 font-mono text-xs">{m.model}</td>
                  <td className="text-right p-2 tabular-nums">{fmtNum(m.tokens_in)}</td>
                  <td className="text-right p-2 tabular-nums">{fmtNum(m.tokens_out)}</td>
                  <td className="text-right p-2 tabular-nums">{m.calls}</td>
                  <td className="text-right p-2 tabular-nums">{fmtPLN(m.cost_pln)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t(lang, 'Per endpoint (top 20)', 'Per endpoint (top 20)')}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-[11px] text-muted-foreground uppercase">
              <tr><th className="text-left p-2">Endpoint</th>
                  <th className="text-right p-2">Calls</th>
                  <th className="text-right p-2">Tokens</th>
                  <th className="text-right p-2">Koszt</th></tr>
            </thead>
            <tbody>
              {data.by_endpoint.map(e => (
                <tr key={e.endpoint} className="border-t border-border">
                  <td className="p-2 font-mono text-xs">{e.endpoint}</td>
                  <td className="text-right p-2 tabular-nums">{e.calls}</td>
                  <td className="text-right p-2 tabular-nums">{fmtNum(e.tokens)}</td>
                  <td className="text-right p-2 tabular-nums">{fmtPLN(e.cost_pln)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ========== SUBSCRIPTION TAB ==========
function SubscriptionTab({ lang, me }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    import('../lib/api').then(api => api.api.get('/billing/status').then(r => setStatus(r.data)).catch(() => setStatus({})));
  }, []);
  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-lg font-medium mb-3">{t(lang, 'Twoja subskrypcja', 'Your subscription')}</h3>
        <pre className="text-xs bg-muted/20 p-3 rounded overflow-auto">{JSON.stringify(status, null, 2)}</pre>
        <Button className="mt-4" variant="outline" onClick={() => window.location.href = '/settings'}>
          {t(lang, 'Zmień plan / wykup', 'Change plan / upgrade')}
        </Button>
      </CardContent>
    </Card>
  );
}

// ========== TENANTS TAB (platform_owner only) ==========
function TenantsTab({ lang }) {
  const [tenants, setTenants] = useState([]);
  const [opts, setOpts] = useState({ industries: [], segments: [] });
  const [scopeFor, setScopeFor] = useState(null);   // tenant being edited
  const [selInd, setSelInd] = useState([]);
  const [selSeg, setSelSeg] = useState([]);

  const reload = () => adminListTenants().then(r => setTenants(r.items || [])).catch(console.error);
  useEffect(() => { reload(); adminScopeOptions().then(setOpts).catch(() => {}); }, []);

  function openScope(tn) {
    setScopeFor(tn);
    setSelInd(tn.allowed_industries || []);
    setSelSeg(tn.allowed_segments || []);
  }
  async function saveScope() {
    try {
      await adminSetTenantScope(scopeFor.id, { allowed_industries: selInd, allowed_segments: selSeg });
      toast.success(t(lang, 'Pas przydzielony', 'Scope assigned'));
      setScopeFor(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  }
  const tog = (arr, set, k) => set(arr.includes(k) ? arr.filter(x => x !== k) : [...arr, k]);

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="text-[11px] text-muted-foreground uppercase">
            <tr><th className="text-left p-2">{t(lang, 'Nazwa', 'Name')}</th>
                <th className="text-left p-2">Plan</th>
                <th className="text-right p-2">Users</th>
                <th className="text-left p-2">Pas (branże / segmenty)</th>
                <th className="text-right p-2">Tokens / month</th>
                <th className="text-left p-2"></th></tr>
          </thead>
          <tbody>
            {tenants.map(tn => (
              <tr key={tn.id} className="border-t border-border">
                <td className="p-2 font-medium">{tn.name}</td>
                <td className="p-2"><Badge variant="outline">{tn.license?.plan || 'trial'}</Badge></td>
                <td className="text-right p-2 tabular-nums">{tn.user_count}</td>
                <td className="p-2 text-xs text-muted-foreground">
                  {(tn.allowed_industries?.length || tn.allowed_segments?.length)
                    ? `${(tn.allowed_industries || []).join(', ') || 'wszystkie branże'} · ${(tn.allowed_segments?.length || 0)} segm.`
                    : <span className="opacity-50">bez ograniczeń</span>}
                </td>
                <td className="text-right p-2 tabular-nums">
                  {fmtNum(tn.tokens_used_current_month)} / {fmtNum(tn.pool_total)}
                </td>
                <td className="p-2">
                  <Button size="sm" variant="outline" onClick={() => openScope(tn)}>Pas…</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={!!scopeFor} onOpenChange={(o) => !o && setScopeFor(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Przydziel pas: {scopeFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Puste = bez ograniczeń. Klient zobaczy/wybierze tylko zaznaczone branże i segmenty ICP.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wide">Branże</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                {opts.industries.map(i => (
                  <button key={i.key} type="button" onClick={() => tog(selInd, setSelInd, i.key)}
                    className={`text-left text-sm px-2.5 py-1.5 rounded-md border transition-colors ${
                      selInd.includes(i.key) ? 'border-primary/60 bg-primary/10' : 'border-border hover:border-primary/30'}`}>
                    {i.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Segmenty ICP</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                {opts.segments.map(s => (
                  <button key={s.key} type="button" onClick={() => tog(selSeg, setSelSeg, s.key)}
                    className={`text-left text-sm px-2.5 py-1.5 rounded-md border transition-colors ${
                      selSeg.includes(s.key) ? 'border-primary/60 bg-primary/10' : 'border-border hover:border-primary/30'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setSelInd([]); setSelSeg([]); }}>Wyczyść (bez ograniczeń)</Button>
              <Button onClick={saveScope}>Zapisz pas</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ========== AUDIT TAB ==========
function AuditTab({ lang }) {
  const [items, setItems] = useState([]);
  useEffect(() => { adminAuditLog(100).then(r => setItems(r.items || [])).catch(console.error); }, []);
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="text-[11px] text-muted-foreground uppercase">
            <tr><th className="text-left p-2">{t(lang, 'Czas', 'Time')}</th>
                <th className="text-left p-2">Actor</th>
                <th className="text-left p-2">Action</th>
                <th className="text-left p-2">Target</th>
                <th className="text-left p-2">Details</th></tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-t border-border">
                <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">{r.created_at?.slice(0, 19).replace('T', ' ')}</td>
                <td className="p-2 text-xs">{r.actor_email}</td>
                <td className="p-2 font-mono text-xs">{r.action}</td>
                <td className="p-2 text-xs text-muted-foreground">{r.target_id?.slice(0, 12) || '—'}</td>
                <td className="p-2 text-xs text-muted-foreground">{JSON.stringify(r.details || {}).slice(0, 80)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
